<script setup>
// Envia um evento de calendário do WhatsApp pela conversa aberta.
// O botão do rodapé abre isto; o envio só acontece no confirmar.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import MessageApi from 'dashboard/api/inbox/message';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const emit = defineEmits(['sent']);

const { t } = useI18n();
const dialogRef = ref(null);
const sending = ref(false);
const formError = ref('');
const name = ref('');
const date = ref('');
const time = ref('');
const durationMinutes = ref(60);
const location = ref('');
const description = ref('');

const canSend = computed(
  () => name.value.trim() && date.value && time.value && !sending.value
);

const pad = value => String(value).padStart(2, '0');

const defaultSlot = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 60, 0, 0);
  const rounded = now.getMinutes() < 30 ? 30 : 60;
  if (rounded === 60) {
    now.setHours(now.getHours() + 1, 0, 0, 0);
  } else {
    now.setMinutes(30, 0, 0);
  }
  date.value = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  time.value = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const reset = () => {
  name.value = '';
  location.value = '';
  description.value = '';
  durationMinutes.value = 60;
  formError.value = '';
  sending.value = false;
  defaultSlot();
};

const open = () => {
  reset();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const startsAtDate = () => new Date(`${date.value}T${time.value}:00`);

const submit = async () => {
  if (!canSend.value) return;
  const starts = startsAtDate();
  if (Number.isNaN(starts.getTime())) {
    formError.value = t('CONVERSATION.WAHA_EVENT.INVALID_TIME');
    return;
  }
  const ends = new Date(
    starts.getTime() + Number(durationMinutes.value) * 60_000
  );
  sending.value = true;
  formError.value = '';
  try {
    const { data } = await MessageApi.sendWahaEvent(props.conversationId, {
      name: name.value.trim(),
      starts_at: starts.toISOString(),
      ends_at: ends.toISOString(),
      location: location.value.trim() || undefined,
      description: description.value.trim() || undefined,
    });
    emit('sent', data);
    useAlert(t('CONVERSATION.WAHA_EVENT.SENT'));
    close();
  } catch (error) {
    formError.value =
      error?.response?.data?.error === 'waha_failed'
        ? t('CONVERSATION.WAHA_EVENT.FAILED')
        : t('CONVERSATION.WAHA_EVENT.FAILED');
  } finally {
    sending.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="md"
    :title="t('CONVERSATION.WAHA_EVENT.TITLE')"
    :description="t('CONVERSATION.WAHA_EVENT.DESCRIPTION')"
    :confirm-button-label="t('CONVERSATION.WAHA_EVENT.SEND')"
    :cancel-button-label="t('CONVERSATION.WAHA_EVENT.CANCEL')"
    :disable-confirm-button="!canSend"
    :is-loading="sending"
    @confirm="submit"
  >
    <div class="flex flex-col gap-3">
      <Input
        v-model="name"
        :label="t('CONVERSATION.WAHA_EVENT.NAME')"
        :placeholder="t('CONVERSATION.WAHA_EVENT.NAME_PLACEHOLDER')"
        autofocus
      />
      <div class="grid grid-cols-2 gap-3">
        <Input
          v-model="date"
          type="date"
          :label="t('CONVERSATION.WAHA_EVENT.DATE')"
        />
        <Input
          v-model="time"
          type="time"
          :label="t('CONVERSATION.WAHA_EVENT.TIME')"
        />
      </div>
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        <span>{{ t('CONVERSATION.WAHA_EVENT.DURATION') }}</span>
        <select
          v-model.number="durationMinutes"
          class="h-10 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm"
        >
          <option :value="30">
            {{ t('CONVERSATION.WAHA_EVENT.DURATION_30') }}
          </option>
          <option :value="60">
            {{ t('CONVERSATION.WAHA_EVENT.DURATION_60') }}
          </option>
          <option :value="90">
            {{ t('CONVERSATION.WAHA_EVENT.DURATION_90') }}
          </option>
        </select>
      </label>
      <Input
        v-model="location"
        :label="t('CONVERSATION.WAHA_EVENT.LOCATION')"
        :placeholder="t('CONVERSATION.WAHA_EVENT.LOCATION_PLACEHOLDER')"
      />
      <Input
        v-model="description"
        :label="t('CONVERSATION.WAHA_EVENT.NOTES')"
        :placeholder="t('CONVERSATION.WAHA_EVENT.NOTES_PLACEHOLDER')"
      />
      <p v-if="formError" class="mb-0 text-sm text-n-ruby-9">
        {{ formError }}
      </p>
    </div>
  </Dialog>
</template>
